/* eslint-disable import/prefer-default-export */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { AdminApiPaths } from '../../../api/APIPaths';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { ICourse } from '../CoursesSlice';
import {
  ISection,
  TLessonTypes,
  TOnBehalfOfUserRole,
  TMeetingSectionPaymentType,
} from '.';
import { THomeworkFile, THomeworkQuiz } from '../HomeworksSlice';

const apiDataProvider = new APIDataProvider();

// SECTION
export type TGetCourseSectionsAsAdmin = {
  id: string;
};

export type TCreateCourseSectionAsAdmin = {
  sortingKey: number;
  course: string;
  title: string;
  description?: string;
  parent?: string;
  template?: string;
};

export type TUpdateSectionAsAdmin = {
  _id: string;
  lessonType?: TLessonTypes;
  dateTime?: string;
  duration?: number;
};

export type TCreateNewCourseSectionAsAdmin = {
  sortingKey: number;
  course: string;
  title?: string;
  description?: string;
  parent?: string;
  template?: string;
};

export type TUpdateCourseSectionAsAdmin = {
  _id: string;
  course: string;
  sortingKey?: number;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
};

const createNewCourseSectionAsAdminModule = async (
  data: TCreateNewCourseSectionAsAdmin
) => {
  const path = `${AdminApiPaths.sections}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const getAdminCourseSectionsByCourseId = createAsyncThunk(
  '/admin/courses/id/sections',
  async (id: string) => {
    const path = `${AdminApiPaths.courses}/${id}?subSections=1`;
    const response = await apiDataProvider.get<TApiResponse<ICourse>>(path);

    return response.data;
  }
);

export const deleteCourseSectionByIdAsAdmin = createAsyncThunk(
  '/admin/sections/deleteOne',
  async (id: string) => {
    const path = `${AdminApiPaths.sections}/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const updateCourseSectionAsAdmin = createAsyncThunk(
  '/admin/sections/updateOneSection',
  async ({ _id, ...data }: TUpdateSectionAsAdmin) => {
    const path = `${AdminApiPaths.sections}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const getSectionByIdAsAdmin = createAsyncThunk(
  '/admin/sections/getOne',
  async (data: { id: string }) => {
    const path = `${AdminApiPaths.sections}/${data.id}`;
    const response = await apiDataProvider.get<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const createNewCourseSectionAsAdmin = createAsyncThunk(
  '/admin/sections/create',
  createNewCourseSectionAsAdminModule
);

export const createNewCourseSectionAsAdminFromTemplate = createAsyncThunk(
  '/admin/sections/template/create',
  createNewCourseSectionAsAdminModule
);

// Teams
export type TCreateCourseTeamSectionAsAdmin = {
  parent: string;
  sortingKey: number;
  course: string;
  title: string;
  dateTime: string;
  duration: number;
  template?: string;
  tutor: string;
  paymentType: TMeetingSectionPaymentType;
  lessonType: TLessonTypes;
};

export type TUpdateCourseTeamsSectionAsAdmin = {
  sectionId: string;
  title?: string;
  tutor?: string;
};

export type TRescheduleCourseTeamsSectionAsAdmin = {
  sectionId: string;
  reason: string;
  start: string;
  end: string;
  duration: number;
  title: string;
  onBehalfOfUserRole?: TOnBehalfOfUserRole;
};

export type TCancelCourseTeamsSectionAsAdmin = {
  _id: string;
  reason: string;
  notifyUser?: boolean;
  onBehalfOfUserRole?: TOnBehalfOfUserRole;
};

const createNewCourseTeamSectionAsAdminModule = async (
  data: TCreateCourseTeamSectionAsAdmin
) => {
  const path = `${AdminApiPaths.teamMeetings}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const updateCourseTeamsSectionAsAdmin = createAsyncThunk(
  '/admin/sections/updateOneTeam',
  async (data: TUpdateCourseTeamsSectionAsAdmin) => {
    const path = `${AdminApiPaths.teamMeetings}/${data.sectionId}`;

    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const rescheduleCourseTeamsSectionAsAdmin = createAsyncThunk(
  '/admin/sections/rescheduleOneTeam',
  async (data: TRescheduleCourseTeamsSectionAsAdmin) => {
    const path = `${AdminApiPaths.teamMeetings}/reschedule/${data.sectionId}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const cancelCourseTeamsSectionAsAdmin = createAsyncThunk(
  '/admin/sections/cancelOneTeam',
  async (data: TCancelCourseTeamsSectionAsAdmin) => {
    const path = `${AdminApiPaths.teamMeetings}/cancel/${data._id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const deleteCourseTeamSectionByIdAsAdmin = createAsyncThunk(
  '/admin/sections/deleteOneTeam',
  async (id: string) => {
    const path = `${AdminApiPaths.teamMeetings}/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const createNewCourseTeamSectionAsAdmin = createAsyncThunk(
  '/admin/sections/createTeam',
  createNewCourseTeamSectionAsAdminModule
);

export const createNewCourseTeamsSectionAsAdminFromTemplate = createAsyncThunk(
  '/admin/sections/template/createTeam',
  createNewCourseTeamSectionAsAdminModule
);

// ZOOM
export type TCreateCourseZoomSectionAsAdmin = {
  parent: string;
  sortingKey: number;
  course: string;
  title: string;
  dateTime: string;
  duration: number;
  template?: string;
  tutor: string;
  paymentType: TMeetingSectionPaymentType;
  lessonType: TLessonTypes;
};

export type TCancelCourseZoomSectionAsAdmin = {
  _id: string;
  reason: string;
  notifyUser?: boolean;
  onBehalfOfUserRole?: TOnBehalfOfUserRole;
};

export type TRescheduleCourseZoomSectionAsAdmin = {
  sectionId: string;
  reason: string;
  start: string;
  end: string;
  duration: number;
  title: string;
  onBehalfOfUserRole?: TOnBehalfOfUserRole;
};

export type TUpdateCourseZoomSectionAsAdmin = {
  sectionId: string;
  title?: string;
  tutor?: string;
};

const createNewCourseZoomSectionAsAdminModule = async (
  data: TCreateCourseZoomSectionAsAdmin
) => {
  const path = `${AdminApiPaths.zoomMeetings}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const deleteCourseZoomSectionByIdAsAdmin = createAsyncThunk(
  '/admin/sections/deleteOneZoom',
  async (id: string) => {
    const path = `${AdminApiPaths.zoomMeetings}/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const cancelCoursZoomeSectionAsAdmin = createAsyncThunk(
  '/admin/sections/cancelOneZoom',
  async (data: TCancelCourseZoomSectionAsAdmin) => {
    const path = `${AdminApiPaths.zoomMeetings}/cancel/${data._id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const rescheduleCoursZoomeSectionAsAdmin = createAsyncThunk(
  '/admin/sections/rescheduleOneZoom',
  async (data: TRescheduleCourseZoomSectionAsAdmin) => {
    const path = `${AdminApiPaths.zoomMeetings}/reschedule/${data.sectionId}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateCourseZoomSectionAsAdmin = createAsyncThunk(
  '/admin/sections/updateOneZoom',
  async (data: TUpdateCourseZoomSectionAsAdmin) => {
    const path = `${AdminApiPaths.zoomMeetings}/${data.sectionId}`;

    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const createNewCourseZoomSectionAsAdmin = createAsyncThunk(
  '/admin/sections/createZoom',
  createNewCourseZoomSectionAsAdminModule
);

export const createNewCourseZoomSectionAsAdminFromTemplate = createAsyncThunk(
  '/admin/sections/template/createZoom',
  createNewCourseZoomSectionAsAdminModule
);

// RESOURCE
export type TCreateCourseResourceSectionAsAdmin = {
  parent: string;
  sortingKey: number;
  course: string;
  curriculum: string;
  grade: string;
  subject: string;
  subjectCode: string;
  subjectLevel: string;
  quiztopiyaTopicId: string;
  quiztopiyaSubTopicId: string;
  quiztopiyaTopicName: string;
  quiztopiyaSubTopicName: string;
  template?: string;
};

export type TUpdateCourseResourceSectionAsAdmin = {
  _id: string;
  sortingKey?: number;
  curriculum: string;
  grade: string;
  subject: string;
  subjectCode: string;
  subjectLevel: string;
  quiztopiyaTopicId: string;
  quiztopiyaSubTopicId: string;
  quiztopiyaTopicName: string;
  quiztopiyaSubTopicName: string;
};

const createNewCourseResourceSectionAsAdminModule = async (
  data: TCreateCourseResourceSectionAsAdmin
) => {
  const path = `${AdminApiPaths.resources}`;
  const response = await apiDataProvider.post<TApiResponse<ISection>>(
    path,
    data
  );

  return response.data;
};

export const deleteCourseResourceSectionByIdAsAdmin = createAsyncThunk(
  '/admin/sections/deleteOneResource',
  async (id: string) => {
    const path = `${AdminApiPaths.resources}/${id}`;
    const response = await apiDataProvider.delete<TApiResponse<ISection>>(path);

    return response.data;
  }
);

export const updateCourseResourceSectionAsAdmin = createAsyncThunk(
  '/admin/sections/updateOneResource',
  async (data: TUpdateCourseResourceSectionAsAdmin) => {
    const path = `${AdminApiPaths.resources}/${data._id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const createNewCourseResourceSectionAsAdmin = createAsyncThunk(
  '/admin/sections/createResource',
  createNewCourseResourceSectionAsAdminModule
);

export const createNewCourseResourceSectionAsAdminForTemplate = createAsyncThunk(
  '/admin/sections/createResource',
  createNewCourseResourceSectionAsAdminModule
);

// HOMEWORK
export type TCreateCourseHomeworkSectionAsAdmin = {
  parent: string;
  sortingKey: number;
  course: string;
  title: string;
  dueDate: string;
  description: string;
  link?: string;
  files?: Array<THomeworkFile>;
  quizzes?: Array<THomeworkQuiz>;
};

export type TUpdateCourseHomeworkSectionAsAdmin = {
  _id: string;
  title: string;
  dueDate: string;
  description: string;
};

export const createCourseHomeworkSectionAsAdmin = createAsyncThunk(
  '/admin/sections/createHomework',
  async (data: TCreateCourseHomeworkSectionAsAdmin) => {
    const path = AdminApiPaths.homeworks;
    const response = await apiDataProvider.post<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);

export const updateCourseHomeworkSectionAsAdmin = createAsyncThunk(
  '/admin/sections/updateHomework',
  async ({ _id, ...data }: TUpdateCourseHomeworkSectionAsAdmin) => {
    const path = `${AdminApiPaths.homeworks}/${_id}`;
    const response = await apiDataProvider.put<TApiResponse<ISection>>(
      path,
      data
    );

    return response.data;
  }
);
